/**
 * CaseDiscoveryRequestsPanel. Each request the firm sent on this case.
 *
 * Sits under CaseDiscoveryPanel. That panel is the DS/DR summary on the journey chart. This list
 * is the full record: what was asked for, who got it, and whether anything is still owed.
 *
 * Add and reload only. Dates on the summary panel above stay empty unless staff type them there.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DiscoveryRequestApi, {
  CaseDiscoveryRequestDto,
  DISCOVERY_REQUEST_RECIPIENTS,
  DISCOVERY_REQUEST_RECIPIENT_LABELS,
  DISCOVERY_REQUEST_STATUS_LABELS,
  DISCOVERY_REQUEST_TYPES,
  DISCOVERY_REQUEST_TYPE_LABELS,
  DISCOVERY_SERVICE_METHODS,
  DISCOVERY_SERVICE_METHOD_LABELS,
  DiscoveryRequestRecipient,
  DiscoveryRequestStatus,
  DiscoveryRequestType,
  DiscoveryServiceMethod,
} from '../../api/DiscoveryRequestApi';

interface CaseDiscoveryRequestsPanelProps {
  Ticket_guid: string;
}

const emptyForm = {
  type: 'ARTICLE_39_14' as DiscoveryRequestType,
  recipient: 'COURT' as DiscoveryRequestRecipient,
  requestedAt: '',
  note: '',
};

function toDateLabel(value: string | null): string {
  if (!value) return 'No request date';
  return value.length >= 10 ? value.slice(0, 10) : value;
}

/** A `date` column arrives as YYYY-MM-DD; a timestamp would not. Trim either to the input. */
function toDateInput(value: string | null): string {
  if (!value) return '';
  return value.length >= 10 ? value.slice(0, 10) : value;
}

/**
 * How service reads on a row.
 *
 * A date with no method, or a method with no date, is a half-recorded fact and is shown as such
 * rather than tidied into looking complete. A certificate of service is signed off both.
 */
function describeService(row: CaseDiscoveryRequestDto): string {
  const servedAt = row.CaseDiscoveryRequest_ServedAt;
  const method = row.CaseDiscoveryRequest_ServedMethod as DiscoveryServiceMethod | null;
  if (!servedAt && !method) return 'Not served yet';
  const when = servedAt ? toDateInput(servedAt) : 'date not recorded';
  const how = method ? DISCOVERY_SERVICE_METHOD_LABELS[method] ?? method : 'method not recorded';
  return `Served ${when} · ${how}`;
}

const CaseDiscoveryRequestsPanel: React.FC<CaseDiscoveryRequestsPanelProps> = ({
  Ticket_guid,
}) => {
  const [requests, setRequests] = useState<CaseDiscoveryRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  /*
   * Service is recorded one request at a time, so the open editor is held by guid rather than a
   * flag. A certificate of service is signed off the served date and method, and until now neither
   * could be entered anywhere — the PATCH route accepted them and nothing called it.
   */
  const [servingGuid, setServingGuid] = useState<string | null>(null);
  const [serveForm, setServeForm] = useState<{
    servedAt: string;
    method: DiscoveryServiceMethod | '';
  }>({ servedAt: '', method: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await DiscoveryRequestApi.list(Ticket_guid);
      setRequests(result.requests || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the discovery requests');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      await DiscoveryRequestApi.create(Ticket_guid, {
        CaseDiscoveryRequest_Type: form.type,
        CaseDiscoveryRequest_Recipient: form.recipient,
        CaseDiscoveryRequest_RequestedAt: form.requestedAt || null,
        CaseDiscoveryRequest_Note: form.note.trim() || null,
      });
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record the discovery request');
    } finally {
      setSaving(false);
    }
  };

  const openServe = (row: CaseDiscoveryRequestDto) => {
    setServingGuid(row.CaseDiscoveryRequest_guid);
    setServeForm({
      servedAt: toDateInput(row.CaseDiscoveryRequest_ServedAt),
      method: (row.CaseDiscoveryRequest_ServedMethod as DiscoveryServiceMethod) || '',
    });
  };

  const saveServed = async (requestGuid: string) => {
    setSaving(true);
    setError(null);
    try {
      await DiscoveryRequestApi.update(Ticket_guid, requestGuid, {
        // Empty clears rather than leaves, so a date entered by mistake can be taken back off.
        CaseDiscoveryRequest_ServedAt: serveForm.servedAt || null,
        CaseDiscoveryRequest_ServedMethod: serveForm.method || null,
      });
      setServingGuid(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record service');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Requests on this case
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each request the firm sent, and who it went to. This is separate from the dates above.
        Those dates stay empty unless you type them there.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : requests.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No discovery requests recorded on this case yet.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {requests.map((row) => {
            const status = (row.status || 'draft') as DiscoveryRequestStatus;
            return (
              <Box
                key={row.CaseDiscoveryRequest_guid}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {DISCOVERY_REQUEST_TYPE_LABELS[row.CaseDiscoveryRequest_Type as DiscoveryRequestType] ??
                    row.CaseDiscoveryRequest_Type}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {DISCOVERY_REQUEST_RECIPIENT_LABELS[
                    row.CaseDiscoveryRequest_Recipient as DiscoveryRequestRecipient
                  ] ?? row.CaseDiscoveryRequest_Recipient}
                  {' · '}
                  {toDateLabel(row.CaseDiscoveryRequest_RequestedAt)}
                </Typography>
                {row.CaseDiscoveryRequest_Note && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {row.CaseDiscoveryRequest_Note}
                  </Typography>
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 0.5, flexWrap: 'wrap' }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={DISCOVERY_REQUEST_STATUS_LABELS[status] ?? status}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {describeService(row)}
                  </Typography>
                  {servingGuid !== row.CaseDiscoveryRequest_guid && (
                    <Button size="small" onClick={() => openServe(row)} disabled={saving}>
                      {row.CaseDiscoveryRequest_ServedAt ||
                      row.CaseDiscoveryRequest_ServedMethod
                        ? 'Edit service'
                        : 'Record service'}
                    </Button>
                  )}
                </Stack>

                {servingGuid === row.CaseDiscoveryRequest_guid && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1, flexWrap: 'wrap', alignItems: 'center' }}
                  >
                    <TextField
                      label="Served on"
                      type="date"
                      size="small"
                      value={serveForm.servedAt}
                      onChange={(e) =>
                        setServeForm({ ...serveForm, servedAt: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      select
                      label="How"
                      size="small"
                      sx={{ minWidth: 160 }}
                      value={serveForm.method}
                      onChange={(e) =>
                        setServeForm({
                          ...serveForm,
                          method: e.target.value as DiscoveryServiceMethod | '',
                        })
                      }
                    >
                      <MenuItem value="">Not recorded</MenuItem>
                      {DISCOVERY_SERVICE_METHODS.map((method) => (
                        <MenuItem key={method} value={method}>
                          {DISCOVERY_SERVICE_METHOD_LABELS[method]}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={saving}
                      onClick={() => saveServed(row.CaseDiscoveryRequest_guid)}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      disabled={saving}
                      onClick={() => setServingGuid(null)}
                    >
                      Cancel
                    </Button>
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Add a request
      </Typography>
      <Stack spacing={2} sx={{ maxWidth: 480 }}>
        <TextField
          select
          label="Type"
          size="small"
          value={form.type}
          onChange={(e) =>
            setForm({ ...form, type: e.target.value as DiscoveryRequestType })
          }
        >
          {DISCOVERY_REQUEST_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {DISCOVERY_REQUEST_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Sent to"
          size="small"
          value={form.recipient}
          onChange={(e) =>
            setForm({ ...form, recipient: e.target.value as DiscoveryRequestRecipient })
          }
        >
          {DISCOVERY_REQUEST_RECIPIENTS.map((recipient) => (
            <MenuItem key={recipient} value={recipient}>
              {DISCOVERY_REQUEST_RECIPIENT_LABELS[recipient]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Request date"
          type="date"
          size="small"
          value={form.requestedAt}
          onChange={(e) => setForm({ ...form, requestedAt: e.target.value })}
          InputLabelProps={{ shrink: true }}
          helperText="Leave blank to save as a draft. Does not fill the dates above."
        />
        <TextField
          label="Note"
          size="small"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
        />
        <Box>
          <Button
            variant="contained"
            size="small"
            disabled={saving}
            onClick={add}
          >
            {saving ? 'Saving…' : 'Add request'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default CaseDiscoveryRequestsPanel;
